import { useContext, useState } from 'react';
import { AppData, AppDataList } from '../../types/common';
import useAppData, { useUpdateAppData } from './ups';
import _ from 'lodash';
import {
  AnchorPreferences,
  AnchorPreferencesObj,
  SATargetSizeChart,
  SizeRecommendationData,
  SizeAdvisorResponse,
  SizeRecommendationQuery,
  SizeRecommendationResponse
} from '../../types/sizeAdvisor';
import GlobalContext from '../../contexts/GlobalContext';
import ENDPOINTS from '../../endpoints';
import { MutationResult, QueryResult, useQuery } from 'react-query';
import { Product, Sku } from '../../types/product';
import API from '../../api';
import { useKafkaEventService } from './kafkaEvent';
import { SizeQuizGlobalConstants } from '../../types/sizeQuiz';
import { getCollectionHandle } from '../../utils/product';

const SIZE_REC_ERROR_CODES = {
  defaultError: 'We are currently unable to suggest a size due to missing product information.',
  partiallyRetired: 'Sorry, the advised size is no longer in our collection.',
  sizeNotAvailable: 'Sorry, the size is not available with us.'
};
const useAnchorPreferences = (): {
  anchorPreferencesData: AnchorPreferencesObj;
  updateAnchorPrefernces: (data: AnchorPreferencesObj) => Promise<AppDataList>;
  updateUserSizeQuizStatus: (data: boolean) => Promise<AppDataList>;
  updateAnchorPreferncesForProductType: (data: AnchorPreferences, productType?: string) => void;
  getAnchorPreferencesForProductType: (string) => AnchorPreferences;
  asyncFetchAnchorPreferences: QueryResult<AppDataList, unknown>;
  updateSizeQuizStatusAndAncorPReferances: (data: AnchorPreferencesObj, status: boolean) => Promise<AppDataList>;
  asyncUpdateAnchorPrefernces: MutationResult<AppDataList, unknown>;
  getAncorPreferenceData: () => AnchorPreferencesObj;
  savePartialAnchorPrefDataInSessionStore: (partialDataFromStore: any, key: any, anchorDetailsObj: any, productType: any) => void;
} => {
  const [anchorPreferencesData, setAnchorPreferencesData] = useState(null);
  const globalContextValue = useContext(GlobalContext);
  const { globalContext } = globalContextValue;
  const uuid = globalContext.gb.customer && globalContext.gb.customer.uuid;
  const appDataKeyForAnchorPreferences = SizeQuizGlobalConstants.APP_DATA_KEY_FOR_ANCHOR_PREFERENCES;
  const appDataUserSizeQuizStatus = SizeQuizGlobalConstants.APP_DATA_USER_SIZE_QUIZ_STATUS;
  const onSuccess = (data: AppDataList): void => {
    let anchorPrefernces: AppData = _.find(data, (appData) => appData.key === appDataKeyForAnchorPreferences);

    if (anchorPrefernces) {
      setAnchorPreferencesData(() => JSON.parse(anchorPrefernces.value));
    }
  };
  const createAnchorPreferencesObject = (anchorDetailsObj, savedAnchorPreferncesObj, curProductType) => {
    const productType = curProductType || anchorDetailsObj.productType;

    // eslint-disable-next-line no-param-reassign
    savedAnchorPreferncesObj = savedAnchorPreferncesObj || {};
    savedAnchorPreferncesObj[productType] = savedAnchorPreferncesObj[productType] || {};
    savedAnchorPreferncesObj[productType].anchorSizeBrand = {
      anchorBrand: decodeURIComponent(anchorDetailsObj.anchorBrand),
      anchorSize: decodeURIComponent(anchorDetailsObj.anchorSize),
      anchorSubVendor: decodeURIComponent(anchorDetailsObj.anchorSubVendor),
      anchorType: decodeURIComponent(anchorDetailsObj.anchorType),
      productType: decodeURIComponent(productType)
    };

    return savedAnchorPreferncesObj;
  };
  const savePartialAnchorPrefDataInSessionStore = (partialDataFromStore: any, key: any, anchorDetailsObj: any, productType: any): void => {
    const savedSizeObj = createAnchorPreferencesObject(anchorDetailsObj, partialDataFromStore, productType);
    const stringifiedSavedSizeObj = JSON.stringify(savedSizeObj);

    globalContext.gb.utils.saveToStore(key, stringifiedSavedSizeObj);
  };
  const getAnchorPreferencesForProductType = (productType: string): AnchorPreferences => {
    if (anchorPreferencesData && anchorPreferencesData[productType]) {
      return anchorPreferencesData[productType].anchorSizeBrand;
    }

    return null;
  };
  const getAncorPreferenceData = (): AnchorPreferencesObj => anchorPreferencesData;
  const asyncFetchAnchorPreferences = useAppData({
    onSuccess: onSuccess
  });
  const [updateAppData, asyncUpdateAnchorPrefernces] = useUpdateAppData({
    onSuccess: onSuccess
  });
  const updateAnchorPrefernces = async (data: AnchorPreferencesObj): Promise<AppDataList> => {
    const payload: AppDataList = [{
      key: appDataKeyForAnchorPreferences,
      value: JSON.stringify(data)
    }];

    return updateAppData(payload);
  };
  const updateUserSizeQuizStatus = async (data: boolean): Promise<AppDataList> => {
    const payload: AppDataList = [{
      key: appDataUserSizeQuizStatus,
      value: JSON.stringify(data)
    }];

    return updateAppData(payload);
  };
  const updateAnchorPreferncesForProductType = (data: AnchorPreferences, productType?: string): void => {
    const payload: AppDataList = [{
      key: appDataKeyForAnchorPreferences,
      value: JSON.stringify({
        ...anchorPreferencesData,
        [productType]: {
          anchorSizeBrand: {
            anchorBrand: decodeURIComponent(data.anchorBrand),
            anchorSize: decodeURIComponent(data.anchorSize),
            anchorSubVendor: decodeURIComponent(data.anchorSubVendor),
            anchorType: decodeURIComponent(data.anchorType),
            productType: decodeURIComponent((productType || data.productType))
          }
        }
      })
    }];

    updateAppData(payload);
  };
  const updateSizeQuizStatusAndAncorPReferances = async (data: AnchorPreferencesObj, status: boolean): Promise<AppDataList> => {
    const payload: AppDataList = [{
      key: appDataKeyForAnchorPreferences,
      value: JSON.stringify(data)
    }, {
      key: appDataUserSizeQuizStatus,
      value: JSON.stringify(status)
    }];

    return updateAppData(payload);
    //   utils.saveToStore(appDataKeyForAnchorPreferences + '_' + uuid, (JSON.stringify(data)), true);
  };

  return {
    anchorPreferencesData: anchorPreferencesData,
    updateAnchorPrefernces: updateAnchorPrefernces,
    updateAnchorPreferncesForProductType: updateAnchorPreferncesForProductType,
    getAnchorPreferencesForProductType: getAnchorPreferencesForProductType,
    asyncFetchAnchorPreferences: asyncFetchAnchorPreferences,
    asyncUpdateAnchorPrefernces: asyncUpdateAnchorPrefernces,
    getAncorPreferenceData: getAncorPreferenceData,
    savePartialAnchorPrefDataInSessionStore: savePartialAnchorPrefDataInSessionStore,
    updateUserSizeQuizStatus: updateUserSizeQuizStatus,
    updateSizeQuizStatusAndAncorPReferances: updateSizeQuizStatusAndAncorPReferances
  };
};
const sizeRecErrorHandling = (error: number | string,
  message?: string,
  advisedSize?: string): SizeRecommendationData => {
  const errorCode = String(error);

  return {
    errorCode: String(error),
    errorMessage: message || SIZE_REC_ERROR_CODES[errorCode] || SIZE_REC_ERROR_CODES.defaultError,
    advisedSize: advisedSize || null
  };
};
const getCurrentPage = (): string => {
  const collectionHandle = getCollectionHandle();
  let currentPage = '';

  if (collectionHandle?.includes('curated-collection') || sessionStorage?.getItem('previousUrl')?.includes('curated-collection')) {
    currentPage = (window.gb && window.gb.liquid && window.gb.liquid.template) === 'product' ? 'CuratedCollection_Product' : 'CuratedCollection_Collection';
  } else if (collectionHandle?.includes('/a/closet')) {
    currentPage = 'Closet On-the-Rack';
  } else {
    currentPage = (window.gb && window.gb.liquid && window.gb.liquid.template) === 'product' ? 'ProductPage' : 'CollectionPage';
  }

  return currentPage;
};
const findRecommendedInfoFromRes = (response: SizeAdvisorResponse,
  sizeChartRowId: string[],
  targetProduct: Product,
  userType?: string): SizeRecommendationData => {
  let recommendedSizeData: SizeRecommendationData = {};
  let skuSizeMap: { [index: string]: Sku } = {};
  let targetSizeCharts: SATargetSizeChart[];
  let skus: string[];
  let foundSize: boolean;
  let showError: boolean;
  let errorData: {
    errorCode?: string;
    errorMessage?: string;
  } = {};

  _.forEach(targetProduct.skus, (skuData: Sku) => {
    skuSizeMap[skuData.sku] = skuData;
  });

  if (_.isArray(response.targetSizeCharts)) {
    targetSizeCharts = _.sortBy(response.targetSizeCharts, 'ranking');
  }

  if (targetSizeCharts[0] && targetSizeCharts[0].skus) {
    skus = targetSizeCharts[0].skus;
  }

  _.find(skus, (sku) => {
    if (skuSizeMap[sku]) {
      foundSize = true;
      recommendedSizeData.advisedSize = skuSizeMap[sku].variantSize;

      if (_.includes(targetProduct.partiallyRetiredSkus, sku) ||
      _.includes(targetProduct.partiallyRetiredSkus, sku.toUpperCase())) {
        showError = true;
        recommendedSizeData.greyedOut = true;
        recommendedSizeData.inventoryUnAvailable = true;
        errorData = sizeRecErrorHandling('partiallyRetired', SIZE_REC_ERROR_CODES.partiallyRetired, recommendedSizeData.advisedSize);
      }

      if (skuSizeMap[sku] && skuSizeMap[sku].retired) {
        recommendedSizeData.retired = true;
        recommendedSizeData.greyedOut = true;
        recommendedSizeData.inventoryUnAvailable = true;
      } else if (userType === 'subscriber') {
        if (!skuSizeMap[sku].inventoryAvailable) {
          recommendedSizeData.inventoryUnAvailable = true;
          recommendedSizeData.greyedOut = true;
        } else {
          recommendedSizeData.inventoryUnAvailable = false;
          recommendedSizeData.greyedOut = !skuSizeMap[sku].closetable;
        }
      } else {
        recommendedSizeData.greyedOut = false;
        recommendedSizeData.inventoryUnAvailable = false;
      }

      return true;
    }

    return false;
  });

  if (!foundSize && skus.length > 0) {
    recommendedSizeData.advisedSize = targetSizeCharts[0].size;
    showError = true;
    errorData = sizeRecErrorHandling('sizeNotAvailable');
  }

  if (!showError) {
    recommendedSizeData.sizeChartRowId = sizeChartRowId;
  } else {
    recommendedSizeData = _.extend(recommendedSizeData, errorData);
  }

  return recommendedSizeData;
};
const extractRecommendedSize = (response: SizeAdvisorResponse,
  targetProduct: Product, userType?: string): SizeRecommendationData => {
  let id;
  const sizeChartRowId: string[] = [];

  if (response.status.code === 0 && response.targetSizeCharts) {
    try {
      const targetSizeCharts = response.targetSizeCharts;

      _.forEach(targetSizeCharts, (targetSizeChart) => {
        id = targetProduct.vendor + '_' + targetSizeChart.subVendor + '_table';
        /* eslint-disable no-useless-escape */
        // removing special char from id
        id = id.replaceAll(' ', '_').replace(/\./g, '_').replace(/\+/g, '_').replace(/\&/g, '_')
          .replace(
            /\'/g, '_'
          )
          .replace(
            /\(/g, '_'
          )
          .replace(/\)/g, '_');
        id = '.' + id + ' #size-' + targetSizeChart.size.replace(/\./g, '_').replace(/\+/g, '_').replace(/\&/g, '_')
          .replace(
            /\'/g, '_'
          )
          .replace(/\(/g, '_')
          .replace(/\)/g, '_');
        /* eslint-enable no-useless-escape */
        sizeChartRowId.push(id.toLowerCase());
      });

      return findRecommendedInfoFromRes(response, sizeChartRowId, targetProduct, userType);
    } catch (e) {
      return sizeRecErrorHandling('default');
    }
  }

  return sizeRecErrorHandling(response.status.code, response.status.sfUserMessage);
};

export const useSizeChartRecommendation = (options: {
  targetProduct: Product;
  selectedVendor: string;
  selectedSize: string;
  selectedSubVendor: string;
  selectedType: string;
},
enabled = false,
onSuccess: (data: SizeRecommendationData) => void): QueryResult<SizeRecommendationData> => {
  const { globalContext } = useContext(GlobalContext);
  const endPoints = ENDPOINTS();
  let sizeChartRecommendationData: SizeRecommendationData;
  const appDataKeyForInitialSizeReccInfoDivDisplay = 'initialSizeReccInfoDivDisplay';
  const [firstTimeSizeRecommended, setFirstTimeSizeRecommended] = useState(false);
  const [updateAppData] = useUpdateAppData({});
  const { postSizeAdvisorEvent } = useKafkaEventService();
  const updateInitialSizeReccInfoDivDisplay = (data: boolean): void => {
    const payload: AppDataList = [{
      key: appDataKeyForInitialSizeReccInfoDivDisplay,
      value: String(data)
    }];

    updateAppData(payload);
  };

  useAppData({
    onSuccess: (data: AppDataList): void => {
      let firstTimeSizeRecommendedData: AppData = _.find(data,
        (appData) => appData.key === appDataKeyForInitialSizeReccInfoDivDisplay) || null;

      if (!firstTimeSizeRecommendedData || (firstTimeSizeRecommendedData && firstTimeSizeRecommendedData.value !== 'true')) {
        setFirstTimeSizeRecommended(true);
        updateInitialSizeReccInfoDivDisplay(true);
      }
    }
  });

  const generateSKUArray = (skus: Sku[]): string[] => {
    const skuArray: string[] = [];

    _.each(skus, function (obj) {
      skuArray.push(obj.sku);
    });

    return skuArray;
  };
  const pushGAEventSysSizeAdviseGenerated = (eventObj: {
    sizeAdviseGenerated: boolean;
  }): void => {
    const sizeAdviseGenerated = eventObj.sizeAdviseGenerated;
    const currentPage = getCurrentPage();

    globalContext.gtm.pushVersionedEvent(
      '2.2',
      currentPage,
      'SysSizeAdviseGenerated',
      'sizeAdviseGenerated=' + sizeAdviseGenerated
    );
  };
  const pushGAEventSysSizeAdvisedAvailable = (eventObj: {
    sizeAdvisedAvailable: boolean;
    reason?: string;
  }): void => {
    const sizeAdvisedAvailable = eventObj.sizeAdvisedAvailable;
    const currentPage = getCurrentPage();

    globalContext.gtm.pushVersionedEvent(
      '2.2',
      currentPage,
      'SysSizeAdvisedAvailable',
      'sizeAdvisedAvailable=' + sizeAdvisedAvailable + ', Reason=' + (eventObj.reason || 'NA'),
    );
  };
  // eslint-disable-next-line complexity
  const fetchSizeChartRecommendationData = async (): Promise<SizeRecommendationData> => {
    const style = options && options.targetProduct && options.targetProduct.style;
    const userType = globalContext && globalContext.customer && globalContext.customer.type;
    let sizeAdvisorUrl = `${endPoints.sizeAdvisor(style)}?cluster=${globalContext.gb.utils.getClusterValue()}&sourceEventId=${globalContext.emitter.sourceEventId}`;
    let advisedSizeAvailableInInventoryInCirculation: boolean | 'null' = 'null';
    let advisedSizeAvailableForClosetingNow: boolean | 'null' = 'null';
    const { data } = await API.get(sizeAdvisorUrl, {
      method: 'GET',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (data && data.recommendedSkus && data.recommendedSkus.length > 0) {
      pushGAEventSysSizeAdviseGenerated({ sizeAdviseGenerated: true });
    } else {
      pushGAEventSysSizeAdviseGenerated({ sizeAdviseGenerated: false });
    }

    sizeChartRecommendationData = extractRecommendedSize(data, options.targetProduct, userType);

    if (data && data.recommendedSkus && data.recommendedSkus.length > 0) {
      if (sizeChartRecommendationData && sizeChartRecommendationData.advisedSize) {
        if (sizeChartRecommendationData.retired) {
          pushGAEventSysSizeAdvisedAvailable({
            sizeAdvisedAvailable: false,
            reason: 'retired'
          });
        } else if (userType === 'subscriber') {
          if (sizeChartRecommendationData.inventoryUnAvailable) {
            pushGAEventSysSizeAdvisedAvailable({
              sizeAdvisedAvailable: false,
              reason: 'inventoryUnAvailable'
            });
          } else if (sizeChartRecommendationData.greyedOut) {
            pushGAEventSysSizeAdvisedAvailable({
              sizeAdvisedAvailable: false,
              reason: 'greyedOut'
            });
          } else {
            pushGAEventSysSizeAdvisedAvailable({
              sizeAdvisedAvailable: true,
              reason: 'NA'
            });
          }
        } else {
          pushGAEventSysSizeAdvisedAvailable({
            sizeAdvisedAvailable: true,
            reason: 'NA'
          });
        }
      } else {
        pushGAEventSysSizeAdvisedAvailable({
          sizeAdvisedAvailable: false,
          reason: sizeChartRecommendationData.errorMessage
        });
      }
    } else {
      pushGAEventSysSizeAdvisedAvailable({
        sizeAdvisedAvailable: false,
        reason: data && data.status && data.status.message
      });
    }

    sizeChartRecommendationData.firstTimeSizeRecommendation = firstTimeSizeRecommended;
    sizeChartRecommendationData.sizeAdvisorProvisioned = data && data.sizeAdvisorProvisioned;

    if (sizeChartRecommendationData) {
      advisedSizeAvailableInInventoryInCirculation = !sizeChartRecommendationData.inventoryUnAvailable;
      advisedSizeAvailableForClosetingNow = !sizeChartRecommendationData.greyedOut;
    }

    if (!(sizeChartRecommendationData && sizeChartRecommendationData.advisedSize)) {
      advisedSizeAvailableInInventoryInCirculation = 'null';
      advisedSizeAvailableForClosetingNow = 'null';
    }

    postSizeAdvisorEvent({
      customerUUID: globalContext.customer.uuid,
      context: getCurrentPage(),
      styleColor: style,
      sizeAdvisorProvisioned: data && data.sizeAdvisorProvisioned,
      advisedSize: sizeChartRecommendationData.advisedSize,
      advisedSizeAvailableInInventoryInCirculation: advisedSizeAvailableInInventoryInCirculation,
      advisedSizeAvailableForClosetingNow: advisedSizeAvailableForClosetingNow,
      statusMsg: data && data.status && data.status.message,
      saAlgorithm: data && data.saAlgorithm,
      bucketId: data && data.bucketId
    });

    return sizeChartRecommendationData;
  };

  return useQuery('SizeChartRecommendation', fetchSizeChartRecommendationData, {
    retry: false,
    enabled: enabled,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    onSuccess: onSuccess
  });
};

export default useAnchorPreferences;
